import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import {
  chooseEhrPatient,
  clearEhrPatientAcquisition,
  registerEhrPatient,
  retryEhrPatientAcquisition,
  verifyActiveEhrLaunch,
} from '../../features/ehr-launch/ehrPatientAcquisitionSlice'
import PatientRegistration from '../../features/patient-registration/PatientRegistration'
import { useLogout } from '../../hooks/useLogout'

const Progress = ({ title, detail }: { title: string; detail: string }) => (
  <VStack spacing={4} textAlign="center">
    <Spinner color="blue.500" size="xl" />
    <Heading size="md">{title}</Heading>
    <Text>{detail}</Text>
  </VStack>
)

export const EhrPatientAcquisitionGate = () => {
  const dispatch = useAppDispatch()
  const logout = useLogout()
  const acquisition = useAppSelector((state) => state.ehrPatientAcquisition)
  const interactionBlocked =
    acquisition.focusVerificationRequired ||
    acquisition.registrationBusy ||
    acquisition.duplicateRecheckInProgress

  const exit = async () => {
    const preserveSharedEhrSession = acquisition.status === 'replaced'
    dispatch(clearEhrPatientAcquisition())
    await logout({ mode: 'ehr', preserveSharedEhrSession })
  }

  const focusOverlay = acquisition.focusVerificationRequired ? (
    <Center
      aria-live="polite"
      bg="whiteAlpha.900"
      bottom={0}
      left={0}
      position="absolute"
      right={0}
      role="status"
      top={0}
      zIndex={1}
    >
      {acquisition.focusVerificationInProgress ? (
        <Progress
          title="Checking the active EHR launch"
          detail="RxPad is checking whether this tab still has the active launch."
        />
      ) : (
        <VStack spacing={4} textAlign="center">
          <Heading size="md">The active EHR launch could not be verified</Heading>
          <Text>{acquisition.focusError}</Text>
          <Button onClick={() => dispatch(verifyActiveEhrLaunch())}>Check Again</Button>
          <Button variant="ghost" onClick={() => void exit()}>
            Exit EHR Session
          </Button>
        </VStack>
      )}
    </Center>
  ) : null

  let content
  if (acquisition.status === 'resolving') {
    content = (
      <Progress
        title="Resolving the EHR patient"
        detail="RxPad is checking the current EHR patient before prescribing."
      />
    )
  } else if (acquisition.status === 'selection_required') {
    content = (
      <Box position="relative">
        <Box
          as="fieldset"
          border={0}
          disabled={interactionBlocked}
          inert={interactionBlocked || undefined}
          m={0}
          minW={0}
          p={0}
        >
          <VStack align="stretch" spacing={4}>
            <Heading size="md">Choose the matching RxPad patient</Heading>
            {acquisition.candidates.map((patient) => (
              <Box key={patient.id} borderWidth="1px" borderRadius="md" p={3}>
                <Text fontWeight="bold">
                  {patient.given_names} {patient.family_name}
                </Text>
                <Text>Date of birth: {patient.date_of_birth}</Text>
                <Button mt={2} onClick={() => dispatch(chooseEhrPatient(patient.id))}>
                  Choose
                </Button>
              </Box>
            ))}
            <Button variant="ghost" onClick={() => void exit()}>
              Exit EHR Session
            </Button>
          </VStack>
        </Box>
        {focusOverlay}
      </Box>
    )
  } else if (acquisition.status === 'registration_required') {
    content = (
      <Box position="relative">
        <Box
          as="fieldset"
          border={0}
          disabled={interactionBlocked}
          inert={interactionBlocked || undefined}
          m={0}
          minW={0}
          p={0}
        >
          <VStack align="stretch" spacing={4}>
            <Heading size="md">Review and register the EHR patient</Heading>
            {acquisition.registrationError && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>{acquisition.registrationError}</AlertDescription>
              </Alert>
            )}
            <PatientRegistration
              initialValues={acquisition.draft}
              selectedPrescriber={null}
              hidePrescriberSelection
              isSubmitting={acquisition.registrationBusy}
              registerPatient={(patient) => dispatch(registerEhrPatient(patient))}
            >
              {({ form, submitButton }) => (
                <>
                  {form}
                  <VStack align="stretch" mt={4}>
                    {submitButton}
                    <Button variant="ghost" onClick={() => void exit()}>
                      Exit EHR Session
                    </Button>
                  </VStack>
                </>
              )}
            </PatientRegistration>
          </VStack>
        </Box>
        {acquisition.duplicateRecheckInProgress && (
          <Center
            aria-live="polite"
            bg="whiteAlpha.900"
            bottom={0}
            left={0}
            position="absolute"
            right={0}
            role="status"
            top={0}
            zIndex={1}
          >
            <Progress
              title="Checking for the existing patient"
              detail="RxPad is checking exact matches once while preserving your edits."
            />
          </Center>
        )}
        {focusOverlay}
      </Box>
    )
  } else if (acquisition.status === 'unavailable') {
    content = (
      <VStack align="stretch" spacing={4}>
        <Heading size="md">
          {acquisition.retryable
            ? 'The EHR patient is temporarily unavailable'
            : 'The EHR patient is unavailable for this launch'}
        </Heading>
        {acquisition.retryable && (
          <Button onClick={() => dispatch(retryEhrPatientAcquisition())}>Retry</Button>
        )}
        <Button variant="ghost" onClick={() => void exit()}>
          Exit EHR Session
        </Button>
      </VStack>
    )
  } else if (acquisition.status === 'expired') {
    content = (
      <VStack align="stretch" spacing={4}>
        <Heading size="md">EHR session expired</Heading>
        <Text>Return to the EHR and start a new launch for the patient.</Text>
        <Button onClick={() => void exit()}>Exit EHR Session</Button>
      </VStack>
    )
  } else if (acquisition.status === 'replaced') {
    content = (
      <VStack align="stretch" spacing={4}>
        <Heading size="md">A newer EHR launch is active</Heading>
        <Text>This tab can no longer prescribe for its previous EHR launch.</Text>
        <Button onClick={() => window.location.reload()}>Reload Current Launch</Button>
        <Button variant="ghost" onClick={() => void exit()}>
          Exit EHR Session
        </Button>
      </VStack>
    )
  } else if (acquisition.status === 'resolved' && acquisition.focusVerificationRequired) {
    content = (
      <Box position="relative" minH="240px">
        {focusOverlay}
      </Box>
    )
  } else {
    content = (
      <Progress
        title="Resolving the EHR patient"
        detail="RxPad is preparing the current EHR patient check."
      />
    )
  }

  return (
    <Center minH="70vh" p={6}>
      <Box bg="white" borderRadius="lg" boxShadow="md" maxW="760px" p={6} w="100%">
        {content}
      </Box>
    </Center>
  )
}
